import { LIMITS } from '@athena/shared'
import { ACCEPTED_DOCUMENT_MIME_TYPES } from '@athena/ui'

/**
 * Which PDFs may be sent for reading, and how many at once.
 *
 * Pure, and the sibling of `imageSelection.ts`. What is different is where the
 * authority sits: these files are *sent*, so every rule here is also enforced
 * by the API, and this copy exists to save a tutor a round trip rather than to
 * make any rule hold. The server refuses a sixth document, an oversized one
 * and a file that is not a PDF whatever the browser did or did not check.
 *
 * The type check is the interesting one. A PDF has a four-byte signature at
 * the front, and checking it here means a `.pdf` that is really a video is
 * turned away before megabytes of it cross the network. That is a courtesy,
 * not a security control — the file is untrusted on arrival regardless, and
 * the server opens it in a parser that has its own opinion.
 */

/** The one type the picker offers. */
export const ACCEPTED_PDF_TYPES: readonly string[] = ACCEPTED_DOCUMENT_MIME_TYPES

export const MAX_PDF_BYTES = LIMITS.MAX_PDF_BYTES
export const MAX_PDF_MEGABYTES = MAX_PDF_BYTES / (1024 * 1024)

/**
 * From `@athena/shared`, because the API bounds the same number. Two copies of
 * this would be two answers to "how many documents is too many".
 */
export const MAX_PDFS = LIMITS.MAX_EXTRACTION_PDFS

/**
 * The ceiling on one request's documents together.
 *
 * Checked in the browser as well as on the server, and that is not
 * belt-and-braces — it is the difference between a sentence and a dead
 * request. Base64 costs a third on top, and the HTTP body limit is derived
 * from this same number, so five documents just inside `MAX_PDF_BYTES` would
 * be refused by the body parser before any resolver ran: a 413 that no
 * service, no log and no `userErrors` entry ever sees.
 *
 * That failure has happened here once already, when batched image extraction
 * outgrew a body limit nobody had revisited, and it took a while to find
 * because the browser could only report that the reader was unreachable. This
 * is the check that keeps a tutor on the other side of it.
 */
export const MAX_PDF_BATCH_BYTES = LIMITS.MAX_EXTRACTION_BATCH_BYTES

export type PdfRejectionReason = 'type' | 'size' | 'empty' | 'count' | 'batch'

export interface RejectedPdf {
  name: string
  reason: PdfRejectionReason
}

export interface ScreenedPdfs {
  /** In the order they were given. Order is the order they will be read in. */
  accepted: readonly File[]
  rejected: readonly RejectedPdf[]
}

/**
 * Whether the browser or the file name says PDF.
 *
 * Both, because neither is reliable alone. `File.type` is the browser's own
 * sniff and is the better signal, but some systems hand a PDF over with an
 * empty type — and refusing those would mean a tutor whose file is perfectly
 * good being told it is not a PDF. The extension covers that case; the
 * signature check below covers a file that lies about both.
 */
function claimsToBePdf(file: File): boolean {
  if (ACCEPTED_PDF_TYPES.includes(file.type)) return true
  return file.type === '' && file.name.toLowerCase().endsWith('.pdf')
}

/**
 * Sort a batch of chosen files into the ones worth sending and the ones that
 * are not, given how many are already selected.
 *
 * Rejections are reported rather than dropped, so the tutor is told which file
 * was refused and why. The count limit applies across the whole selection
 * rather than per batch, which is why `alreadySelected` is a parameter.
 */
export function screenPdfFiles(
  files: readonly File[],
  alreadySelected: number,
  /** The bytes already chosen, which the batch ceiling is measured against. */
  alreadySelectedBytes = 0,
): ScreenedPdfs {
  const accepted: File[] = []
  const rejected: RejectedPdf[] = []
  let bytes = alreadySelectedBytes

  for (const file of files) {
    if (!claimsToBePdf(file)) {
      rejected.push({ name: file.name, reason: 'type' })
      continue
    }
    // Before the size check, because "empty" and "too big" are different
    // things to be told and zero bytes is not a size problem.
    if (file.size === 0) {
      rejected.push({ name: file.name, reason: 'empty' })
      continue
    }
    if (file.size > MAX_PDF_BYTES) {
      rejected.push({ name: file.name, reason: 'size' })
      continue
    }
    if (alreadySelected + accepted.length >= MAX_PDFS) {
      rejected.push({ name: file.name, reason: 'count' })
      continue
    }
    // Last, because it is the only rule about the selection rather than about
    // the file: a tutor should be told their file is not a PDF before being
    // told the batch is full.
    if (bytes + file.size > MAX_PDF_BATCH_BYTES) {
      rejected.push({ name: file.name, reason: 'batch' })
      continue
    }
    bytes += file.size
    accepted.push(file)
  }

  return { accepted, rejected }
}

/** Every PDF starts with these five bytes. */
const SIGNATURE = '%PDF-'

/**
 * Whether the bytes really are a PDF, not just labelled as one.
 *
 * Reads the first five bytes and compares them to the signature. A courtesy
 * check: it saves sending a file the server would refuse anyway, and it costs
 * one slice of the head of the file rather than a read of all of it.
 *
 * Anything that cannot be read at all is reported as not a PDF, because a file
 * the browser cannot slice is a file that will not survive being sent either.
 */
export async function looksLikePdf(file: File): Promise<boolean> {
  try {
    const head = await file.slice(0, SIGNATURE.length).arrayBuffer()
    return new TextDecoder('latin1').decode(head) === SIGNATURE
  } catch {
    return false
  }
}

/** Base64 for the request body, without the `data:` prefix. */
export async function toBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer())
  let binary = ''
  // In chunks: `String.fromCharCode(...bytes)` on a five-megabyte array
  // overflows the argument limit and throws.
  const CHUNK = 0x8000
  for (let at = 0; at < bytes.length; at += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(at, at + CHUNK))
  }
  return btoa(binary)
}
